import { Alert, Flex, Spin, Typography } from 'antd';
import {
  DocumentReviewIssues,
  DocumentReviewStatus,
  DocumentReviewType,
  EvidenceSectionName,
  EvidenceTask,
  EvidenceTaskName,
  EvidenceTaskNameMap,
  EvidenceTaskStatus,
} from '@api/brokerCases/types';
import { ApplicantOption } from '@pages/EvidencePage/helpers';
import { EvidenceItemActionListItemLabel } from '@pages/EvidencePage/components/EvidenceList/components/EvidenceItemActionListItem/components/EvidenceItemActionListItemLabel';
import { EvidenceItemActionListItemButtons } from './components/EvidenceItemActionListItemButtons';
import { useDocumentReviews } from '@queries/brokerCases/useDocumentReviews';
import { useMemo } from 'react';
import { DOCUMENT_REVIEW_TYPE_TO_RELEVANT_TASKS } from '@pages/EvidencePage/evidenceConfig';
import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';

function isDocumentReviewIssueTaskName(taskName: EvidenceTaskName): taskName is keyof DocumentReviewIssues {
  return (
    taskName === EvidenceTaskNameMap.LAST_3_MONTHS_BANK_STATEMENTS ||
    taskName === EvidenceTaskNameMap.LATEST_3_MONTHS_PAYSLIPS
  );
}

export interface IEvidenceItemActionListItemProps {
  sectionName: EvidenceSectionName;
  task: EvidenceTask;
  applicants: ApplicantOption[];
  showTooltip: boolean;
  onProgressLoadingChange: (isLoading: boolean) => void;
  isCurrentTask: boolean;
}

export const EvidenceItemActionListItem = ({
  sectionName,
  task,
  applicants,
  showTooltip,
  onProgressLoadingChange,
  isCurrentTask,
}: IEvidenceItemActionListItemProps) => {
  const caseId = task.payload.caseId;
  const individualId = task.payload.individualId;
  const { data: ongoingFinancesReviews } = useDocumentReviews(
    caseId,
    individualId ?? '',
    DocumentReviewStatus.ONGOING,
    DocumentReviewType.CUSTOMER_FINANCES,
  );

  const { data: completedFinancesReviews } = useDocumentReviews(
    caseId,
    individualId ?? '',
    DocumentReviewStatus.COMPLETED,
    DocumentReviewType.CUSTOMER_FINANCES,
  );

  const { isUnderReview, hasFailedReview, issuesForThisTask } = useMemo(() => {
    const isFinancesReviewTask = DOCUMENT_REVIEW_TYPE_TO_RELEVANT_TASKS[DocumentReviewType.CUSTOMER_FINANCES].includes(
      task.name,
    );
    if (!isFinancesReviewTask) {
      return { isUnderReview: false, hasFailedReview: false, issuesForThisTask: [] as string[] };
    }

    if (ongoingFinancesReviews && ongoingFinancesReviews.length > 0) {
      return { isUnderReview: true, hasFailedReview: false, issuesForThisTask: [] as string[] };
    }

    if (
      !isDocumentReviewIssueTaskName(task.name) ||
      !completedFinancesReviews ||
      completedFinancesReviews.length === 0
    ) {
      return { isUnderReview: false, hasFailedReview: false, issuesForThisTask: [] as string[] };
    }

    const issueTaskName: keyof DocumentReviewIssues = task.name;
    const issues = completedFinancesReviews.flatMap(review => review.issues[issueTaskName]);
    return {
      isUnderReview: false,
      hasFailedReview: issues.length > 0,
      issuesForThisTask: issues,
    };
  }, [ongoingFinancesReviews, completedFinancesReviews, task.name]);

  return (
    <Flex vertical className='rounded-lg bg-gray-50 py-3 px-4 gap-3'>
      {isCurrentTask && (
        <Flex vertical className='gap-2'>
          <Flex justify='space-between' align='center'>
            <EvidenceItemActionListItemLabel
              sectionName={sectionName}
              task={task}
              applicants={applicants}
              showTooltip={showTooltip}
              hasFailedReview={hasFailedReview}
              isUnderReview={isUnderReview}
            />
            <Flex align='center' className='gap-2'>
              <Flex className='gap-2 items-center'>
                {isUnderReview && (
                  <>
                    <Spin className='text-green-500' indicator={<LoadingOutlined />} />
                    <Typography.Text className='text-xs font-bold text-gray-500'>Checking documents</Typography.Text>
                  </>
                )}
              </Flex>
              <EvidenceItemActionListItemButtons
                sectionName={sectionName}
                task={task}
                onProgressLoadingChange={onProgressLoadingChange}
                isUnderReview={isUnderReview}
                hasFailedReview={hasFailedReview}
              />
            </Flex>
          </Flex>
          {hasFailedReview && task.status !== EvidenceTaskStatus.COMPLETED && (
            <Alert
              showIcon
              icon={<ExclamationCircleOutlined />}
              message={
                <Typography.Text strong className='font-bold'>
                  Please resolve these issues by reuploading documents
                </Typography.Text>
              }
              type='error'
              description={
                <ul className='mb-0 ps-5'>
                  {issuesForThisTask.map((issue, i) => (
                    <li key={`${i}-${issue}`}>
                      <Typography.Text>{issue}</Typography.Text>
                    </li>
                  ))}
                </ul>
              }
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};
